import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  SegmentedControl,
} from '@vnetwork/ui/primitives';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, FilterBuilder, Pagination, type FilterCondition } from '@vnetwork/ui/data';
import { Filter, Search, X } from 'lucide-react';
import type { CustomerTicketRow } from '@/api/customer-tickets';
import type { CustomerServiceRow } from '@/api/customer-sla';
import { paginationView } from '../../pagination-summary';
import { useTicketColumns } from './tickets-columns';
import { buildTicketFilterFields, matchesTicketConditions } from './tickets-filters';

/** Unresolved/active work; everything else counts as resolved (terminal). */
const ACTIVE_TICKET_STATUSES = new Set(['opening', 'work-in-progress', 'pending', 'escalated']);

type TicketScope = 'all' | 'active' | 'resolved';

function isActive(row: CustomerTicketRow): boolean {
  return ACTIVE_TICKET_STATUSES.has(String(row.ticket_status));
}

export interface TicketsSummaryProps {
  rows: CustomerTicketRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  services: CustomerServiceRow[];
}

/**
 * Section 2 — the tickets summary. The 3-way scope (All / Active / Resolved) sits
 * in the card header; the body has a search + Filters popover (Status / Priority /
 * Product group) over the tickets table, mirroring the Service SLAs card. Active
 * is the default scope. Counts are page-scoped (no per-customer ticket-stat
 * endpoint in scope).
 */
export function TicketsSummary({
  rows,
  total,
  page,
  pageSize,
  pageCount,
  onPageChange,
}: TicketsSummaryProps) {
  const [scope, setScope] = useState<TicketScope>('active');
  const [query, setQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const columns = useTicketColumns();

  const activeCount = rows.filter(isActive).length;
  const resolvedCount = rows.length - activeCount;

  const filterFields = useMemo(() => buildTicketFilterFields(rows), [rows]);
  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  const displayed = useMemo(() => {
    const scoped =
      scope === 'active'
        ? rows.filter(isActive)
        : scope === 'resolved'
          ? rows.filter((r) => !isActive(r))
          : rows;
    const q = query.trim().toLowerCase();
    return scoped.filter((r) => {
      if (!matchesTicketConditions(r, conditions)) return false;
      if (!q) return true;
      const hay = `${r.ticket_type_title ?? ''} ${r.ticket_content ?? ''} ${r.service_name ?? ''} ${r.product_group_name ?? ''} ${r.ticket_id}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, scope, query, conditions]);

  return (
    <Card>
      <CardHeader
        actions={
          <SegmentedControl
            value={scope}
            onValueChange={(v) => setScope(v as TicketScope)}
            aria-label="Filter tickets by status"
            items={[
              { value: 'all', label: `All (${rows.length})` },
              { value: 'active', label: `Active (${activeCount})` },
              { value: 'resolved', label: `Resolved (${resolvedCount})` },
            ]}
          />
        }
      >
        <CardTitle>Tickets ({total})</CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tickets…"
              startSlot={<Search />}
              style={{ maxWidth: 320 }}
            />
          }
          end={
            <>
              {appliedFieldCount ? (
                <Button variant="ghost" startIcon={<X size={15} />} onClick={() => setConditions([])}>
                  Clear
                </Button>
              ) : null}
              {filterFields.length ? (
                <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="secondary" startIcon={<Filter size={15} />}>
                      Filters {appliedFieldCount ? <Badge tone="primary">{appliedFieldCount}</Badge> : null}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" style={{ padding: 0 }}>
                    <FilterBuilder
                      fields={filterFields}
                      defaultValue={conditions.length ? conditions : undefined}
                      onApply={(next) => {
                        setConditions(next.filter((c) => c.value !== ''));
                        setFilterOpen(false);
                      }}
                      onClose={() => setFilterOpen(false)}
                    />
                  </PopoverContent>
                </Popover>
              ) : null}
            </>
          }
        />
        <DataTable
          columns={columns}
          data={displayed}
          enableSorting={false}
          getRowId={(r) => String(r.ticket_id)}
          emptyState={<EmptyState title="No tickets" description="No tickets match this filter." />}
          footer={(() => {
            // Server pagination applies only to the raw, unfiltered "All" page; any
            // scope / search / filter narrowing is a single client-side page. Every
            // view still shows a consistent summary.
            const serverPaged = scope === 'all' && !query.trim() && conditions.length === 0;
            const view = serverPaged
              ? paginationView(page, pageSize, rows.length, total, pageCount)
              : paginationView(1, Math.max(displayed.length, 1), displayed.length, displayed.length, 1);
            return (
              <Pagination
                page={serverPaged ? page : 1}
                pageCount={view.pageCount}
                onPageChange={serverPaged ? onPageChange : () => {}}
                summary={view.summary}
              />
            );
          })()}
        />
      </CardBody>
    </Card>
  );
}
